/*
 * After a turn that was cut off (the connection dropped, the stream went silent, the server
 * restarted) the client does not know what the server kept of it. These helpers find the turn
 * in the conversation as the server has it. Pure, so the matching is testable without a store.
 */

/** The fields of a chat message the matching reads. */
export interface ReconcilableMessage {
  id?: string
  role: 'User' | 'Assistant'
  content: string
  partial?: boolean
}

/**
 * Where a question stands in the conversation, in terms the server's copy can be searched
 * with. A question sent in this session has no server id yet, so it is located from the last
 * message before it that has one.
 */
export interface QuestionPlace {
  /** The question's own server id, when it has one (it was loaded, or reconciled before). */
  id?: string
  /** The last message before the question that has a server id; null when none has. */
  afterId: string | null
  /** The question is the n-th user message after that one (1 = the first). */
  ordinal: number
  content: string
  /** Server ids on screen when the turn was sent: an answer among them is not this turn's. */
  knownIds: string[]
  /** Assistant bubbles under the question when the turn was sent (a retry keeps the earlier attempt's). */
  answersBefore: number
}

/** Describes where `question` (one of `messages`) stands; taken when its turn is sent. */
export const placeOfQuestion = (
  messages: readonly ReconcilableMessage[],
  question: ReconcilableMessage
): QuestionPlace => {
  const index = messages.indexOf(question)
  let anchor = index - 1
  while (anchor >= 0 && !messages[anchor].id) {
    anchor--
  }
  return {
    id: question.id,
    afterId: anchor >= 0 ? (messages[anchor].id as string) : null,
    ordinal: messages
      .slice(anchor + 1, index + 1)
      .filter(m => m.role === 'User').length,
    content: question.content,
    knownIds: messages.flatMap(m => (m.id ? [m.id] : [])),
    answersBefore: messages.slice(index + 1).filter(m => m.role === 'Assistant')
      .length,
  }
}

export interface StoredTurn<T> {
  /** The question as the server stored it. */
  question: T
  /** What the server stored as this turn's answer (finished or cut short); null when nothing. */
  answer: T | null
}

/**
 * Finds the turn in `serverMessages` (the conversation as the server returns it). Null unless
 * the server holds exactly this question, at this place, as the conversation's last question:
 * anything else (the question never arrived, another window went on with the conversation) is
 * not this turn, and nothing may be concluded from it.
 */
export const findStoredTurn = <T extends ReconcilableMessage>(
  serverMessages: readonly T[],
  place: QuestionPlace
): StoredTurn<T> | null => {
  let index = -1
  if (place.id) {
    index = serverMessages.findIndex(m => m.id === place.id)
  } else {
    let start = 0
    if (place.afterId !== null) {
      const anchor = serverMessages.findIndex(m => m.id === place.afterId)
      if (anchor < 0) {
        return null
      }
      start = anchor + 1
    }
    let seen = 0
    for (let i = start; i < serverMessages.length; i++) {
      if (serverMessages[i].role === 'User' && ++seen === place.ordinal) {
        index = i
        break
      }
    }
  }

  const question = serverMessages[index]
  if (
    !question ||
    question.role !== 'User' ||
    question.content !== place.content
  ) {
    return null
  }

  const after = serverMessages.slice(index + 1)
  if (after.some(m => m.role === 'User')) {
    return null
  }

  // Only an answer that was not there when the turn was sent is this turn's answer.
  const last = after[after.length - 1]
  const isNew =
    !!last &&
    after.length > place.answersBefore &&
    !(last.id && place.knownIds.includes(last.id))
  return { question, answer: isNew ? last : null }
}
